import {
  SelectionSet,
  SelectionSetInput,
  SelectionSetLabel,
  SelectionSetTileContents,
  SelectionSetTileName,
  SelectionSetTilePrice,
  StatusMessage,
} from '@vp/swan';
import React, { useState, useCallback, useContext, useEffect } from 'react';
import { ChatbotContext } from '../../../contexts/ChatbotContext';
import { CAContext } from '../../../contexts/CAContext';
import { util } from '../../../utils/miaw/common';
import { DISPLAY_NAME } from '../../../config/app-config';
import PrechatForm from '../../PrechatFormInline/PrechatFormInline';
import { useCustomTracking } from '../../../hooks/custom-hooks/useTracking';
import { useIdentity } from '@vp/auth-react';
import { IIdentityObject, IChatbotSummaryResponse } from '../../../interfaces/app-interfaces';
import { useGenerateAcw } from '../../../hooks/useGenerateAcw';
import { getQuestfromIntent } from '../../../utils/miaw/init-chat-utils';
import { IChatQueueType } from '../../../interfaces/case-details-api-interfaces';
import { useNRLogging } from '../../../hooks/custom-hooks/useNRLogging';
import { getChatSessionType, getOrderNumberForChatbot, getSelectedProductIdsForChatbot } from '../../../utils/session-storage-utils';

interface IButtonGroupProps {
  actionList: {
    label: string;
    value: string;
    description?: string;
    isOptionAvailable?: boolean;
    isPreChatFlowButton?: boolean;
  }[];
  onSelect?: (value: string) => void;
  postion?: 'start' | 'end';
  type: 'buttons' | 'tiles-horizontal';
  typeOfFLow?: 'moneyBack' | 'transferToAgent' | 'nonLoggedInPreChatWithOrderNumber' | 'globalException';
}

const ButtonGroup: React.FC<IButtonGroupProps> = ({ actionList, onSelect, postion = 'end', type, typeOfFLow }) => {
  const [showWarningMessage, setShowWarningMessage] = useState(false);
  const { logCustomAttributesNR } = useNRLogging();

  const {
    chatState: { userInfo },
    setChatState,
  } = useContext(CAContext);
  const {
    state: { sendChatbotMessage, sessionId, selectedProductIds, orderNumber, latestQuery },
    setState,
  } = useContext(ChatbotContext);

  const { sendTrackingEvent } = useCustomTracking();
  const identity = useIdentity();
  const { summaryAPI } = useGenerateAcw();

  const handleStartMIAWChatWithPrechat = useCallback(
    (data: {
      firstName: string;
      lastName: string;
      email: string;
      chatbotContactReason: string;
      orderNumber: string;
      isSummaryGenerated: boolean;
      typeOfFLow: string;
    }) => {
      // Set User info which can be used to start MIAW chat session again
      setChatState((prevState) => ({
        ...prevState,
        userInfo: {
          isLoggedIn: prevState.userInfo?.isLoggedIn ?? false,
          userData: {
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email,
            shopperId: (identity as IIdentityObject).canonicalId || '',
            chatbotContactReason: data?.chatbotContactReason || '',
            orderNumber: data?.orderNumber || '',
          },
        },
      }));
      setState((prevState) => ({
        ...prevState,
        conversationMessages: [
          ...prevState.conversationMessages,
          {
            id: util.generateUUID(),
            messageContent:
              userInfo?.isLoggedIn && userInfo?.userData
                ? data.isSummaryGenerated
                  ? ''
                  : data.chatbotContactReason
                : typeOfFLow === 'nonLoggedInPreChatWithOrderNumber'
                  ? `${data.firstName}\n${data.lastName}\n${data.email}${data.orderNumber ? `\n${data.orderNumber}` : ''}`
                  : `${data.firstName}\n${data.lastName}\n${data.email}`,
            timestamp: new Date().getTime(),
            type: 'user',
            isMessageRead: false,
            showFeedback: false,
          },
        ],
      }));

      // Remove Prechat Form from the conversation
      setState((prevState) => ({
        ...prevState,
        conversationMessages: prevState.conversationMessages.map((message) => {
          if (message.customMessageType === 'prechatMessage') {
            return { ...message, additionalComponents: undefined };
          }
          return message;
        }),
      }));
    },
    [sessionId, setChatState, setState, identity],
  );

  const onOptionSelect = async (value: string) => {
    // Agent connect flow is for talking to specialist

    // Logging which option select was clicked
    logCustomAttributesNR('ca-chatbot-info', {
      logName: 'option_selected',
      logLevel: 'info',
      message: `User selected option: ${value} for flow type: ${typeOfFLow}`,
    });

    if (value === 'agentConnect') {
      sendTrackingEvent('Chatbot Clicked', 'Chatbot:Escalation', 'escalationTriggered');
      // Set User Message

      if (typeOfFLow !== 'transferToAgent' && typeOfFLow !== 'nonLoggedInPreChatWithOrderNumber') {
        setState((prevState) => ({
          ...prevState,
          conversationMessages: [
            ...prevState.conversationMessages,
            {
              id: util.generateUUID(),
              messageContent: 'Chat with a specialist',
              timestamp: new Date().getTime(),
              type: 'user',
              isMessageRead: false,
              showFeedback: false,
            },
          ],
          isSendMessageLoading: true,
        }));
      }

      let isAcwAPIGeneratedSummary;
      try {
        if (getChatSessionType() !== 'miawChatSession') {
          // Showing loading indicator with removing the chat with specialist button
          setState((prev) => {
            const updateConversationMessages = prev.conversationMessages.map((message, index) => {
              if (index === prev.conversationMessages.length - 2) {
                // Remove any custom components from the Conversation after user has selected an option to prevent multiple clicks
                return { ...message, additionalComponents: undefined };
              }
              return message;
            });
            return { ...prev, isSendMessageLoading: true, conversationMessages: [...updateConversationMessages] };
          });
          // API to call summary in case of chat happened, otherwise show empty contact reason
          const response = await summaryAPI();
          // if check response ok
          logCustomAttributesNR('ca-chatbot-info', {
            logName: 'summary_api_success',
            logLevel: 'info',
            message: `Summary API Response: ${response?.intent}`,
          });

          let customQueue: IChatQueueType | undefined;
          if (response?.intent && response?.intent !== 'NA' && response?.intent !== '') {
            // getting queue info from intent
            customQueue = getQuestfromIntent(response?.intent);
          }
          setChatState((prev) => ({
            ...prev,
            chatbotSummaryResponse: response as IChatbotSummaryResponse,
            customQueue: customQueue,
          }));

          isAcwAPIGeneratedSummary = response?.summary == 'NA' || response?.summary == '' ? '' : response?.summary;
          // Final state
          setState((prev) => ({
            ...prev,
            isSendMessageLoading: true,
          }));
        }
      } catch (error) {
        logCustomAttributesNR('ca-chatbot-error', {
          logName: 'summary_api_fail',
          logLevel: 'error',
          message: `summary API Error: ${error}`,
        });
        setState((prev) => ({
          ...prev,
          isSendMessageLoading: false,
        }));
        isAcwAPIGeneratedSummary = false;
        console.error('Error fetching summary:', error);
      }

      const prechatMessage = 'Okay! To connect you with a specialist, I just need a bit more information about why you’re reaching out:';

      const shouldShowMessage =
        (!isAcwAPIGeneratedSummary && typeOfFLow !== 'nonLoggedInPreChatWithOrderNumber') ||
        (isAcwAPIGeneratedSummary && !userInfo?.isLoggedIn && typeOfFLow !== 'nonLoggedInPreChatWithOrderNumber');

      // logs to know which flag is triggering prechat form

      setState((prevState) => ({
        ...prevState,
        isSendMessageLoading: true,
        conversationMessages: [
          ...prevState.conversationMessages,
          {
            id: util.generateUUID(),
            messageContent: shouldShowMessage ? prechatMessage : '',
            timestamp: new Date().getTime(),
            type: 'chatbot',
            isMessageRead: false,
            senderDisplayName: DISPLAY_NAME.CHATBOT,
            additionalComponents: <PrechatForm onSubmit={handleStartMIAWChatWithPrechat} correctSummary={false} typeOfFLow={typeOfFLow} />,
            customMessageType: 'prechatMessage',
            showFeedback: false,
          },
        ],
      }));
      return;
    }
    if (typeOfFLow === 'moneyBack') {
      let orderId, productIds;
      if (orderNumber === undefined || orderNumber === '' || selectedProductIds === undefined || selectedProductIds.length === 0) {
        logCustomAttributesNR('ca-chatbot-error', {
          logName: 'order number not in context',
          logLevel: 'error',
          message: `order number not in context`,
        });
        orderId = getOrderNumberForChatbot();
        productIds = getSelectedProductIdsForChatbot();
      }
      sendChatbotMessage?.({
        type: 'text_message_with_different_query',
        messageData: {
          messageText: value,
          query: `I would prefer ${value} for Order ID - ${orderNumber || orderId} and Product ID - [${selectedProductIds !== undefined ? selectedProductIds : productIds}]`,
        }, // Send query to the chatbot
      });
      onSelect?.(value);
      return;
    }
    if (typeOfFLow === 'globalException') {
      sendChatbotMessage?.({
        type: 'text_message_with_different_query',
        messageData: {
          messageText: value,
          query: value === 'No' ? `exception_decline` : latestQuery,
        }, // Send query to the chatbot
      });
      onSelect?.(value);
      return;
    }
    const isPreChatFlowButton =
      actionList.length === 2 &&
      actionList.some((item) => item.label === 'Yes' && item.value === 'Yes' && item.isPreChatFlowButton) &&
      actionList.some((item) => item.label === 'No' && item.value === 'No' && item.isPreChatFlowButton);

    if (!isPreChatFlowButton) {
      sendChatbotMessage?.({ type: 'text_message', messageData: value });
    }
    if (typeOfFLow !== 'transferToAgent' && typeOfFLow !== 'nonLoggedInPreChatWithOrderNumber') {
      onSelect?.(value);
    }
  };

  useEffect(() => {
    if (actionList.length && typeOfFLow === 'moneyBack') {
      const hasMoneyOptionNotAvailable = actionList.some((item) => item.isOptionAvailable === false);
      if (hasMoneyOptionNotAvailable) {
        setShowWarningMessage(true);
      }
    }
  }, [actionList]);

  useEffect(() => {
    if (typeOfFLow === 'transferToAgent' || typeOfFLow === 'nonLoggedInPreChatWithOrderNumber') {
      onOptionSelect('agentConnect');
      if (typeOfFLow === 'nonLoggedInPreChatWithOrderNumber') {
        logCustomAttributesNR('ca-chatbot-info', {
          logName: 'request_cancelation_flow',
          logLevel: 'info',
          message: `non logged in user requested for cancelation and directly transfer to specialist`,
        });
      } else {
        logCustomAttributesNR('ca-chatbot-info', {
          logName: 'Specialist_Escalation_Flow_Triggered',
          logLevel: 'info',
          message: `SPecialist escalation flow triggered from chatbot`,
        });
      }
    }
  }, []);

  return (
    <>
      {typeOfFLow === 'moneyBack' && showWarningMessage && (
        <StatusMessage skin="warning" marginY="between-actions" style={{ paddingTop: '6px' }}>
          Some solutions are not available
        </StatusMessage>
      )}
      <SelectionSet
        variant="single-select"
        skin={type}
        onSelectedValueChange={(e) => {
          onOptionSelect(e as string);
        }}
        marginTop={'between-actions'}
        style={{ justifyContent: postion }}
        compactMode
      >
        {actionList.map((action) => {
          return (
            <SelectionSetInput
              value={action.value}
              key={action.value}
              disabled={type === 'tiles-horizontal' && typeOfFLow === 'moneyBack' && !action.isOptionAvailable}
            >
              {type === 'buttons' ? (
                <SelectionSetLabel mb={4} style={{ height: 'fit-content' }}>
                  {action.label}
                </SelectionSetLabel>
              ) : (
                <SelectionSetLabel style={{ minHeight: '60px' }}>
                  <SelectionSetTileContents>
                    <SelectionSetTileName>{action.label}</SelectionSetTileName>
                    <SelectionSetTilePrice>{action.description}</SelectionSetTilePrice>
                  </SelectionSetTileContents>
                </SelectionSetLabel>
              )}
            </SelectionSetInput>
          );
        })}
      </SelectionSet>
    </>
  );
};

export default ButtonGroup;
